/**
 * One element of the page shown on the whole screen, and back.
 *
 * A score is watched while it is heard, and the frame around it -- the tabs, the toolbar, the
 * other desk -- is what stands between the reader and the page. Watching also wants the space
 * bar, which is here for the same reason: it has to know which desk is shown.
 *
 * A press fills the window with the element at once (`FILLED`) and asks the browser for its
 * own full screen on top, rather than filling only when the browser says no: an app's
 * embedded browser can leave the request unanswered for good -- neither granted nor refused,
 * no event -- and a button that waits on it does nothing. Filled, everything else of the page
 * is hidden behind the element; granted, the browser's own frame is too. The fill follows the
 * browser whenever it acts, entering or leaving full screen on its own (its Escape, a tab
 * switch), and Escape leaves the fill where the browser has nothing to leave.
 */

import { useCallback, useEffect, useState } from "react";
import type { RefObject } from "react";

export interface Fullscreen {
  /** Whether `ref`'s element is filling the window, and the screen where the browser let it. */
  readonly on: boolean;
  toggle(): void;
}

/**
 * The classes an element takes while `on`: the whole window, above the page. Under the one
 * dialog the page raises over everything (`z-50`), so the keys can still be looked up.
 */
export const FILLED = "fixed inset-0 z-40";

/** The element filling the window, which one page has at most; none, most of the time. */
let filling: HTMLElement | null = null;

export function useFullscreen(ref: RefObject<HTMLElement>): Fullscreen {
  const [filled, setFilled] = useState(false);

  const leave = useCallback(() => {
    setFilled(false);
    if (ref.current !== null && document.fullscreenElement === ref.current) {
      void document.exitFullscreen().catch(() => undefined);
    }
  }, [ref]);

  useEffect(() => {
    let was = false;
    const changed = (): void => {
      const now = document.fullscreenElement === ref.current;
      if (now) setFilled(true);
      else if (was) setFilled(false);
      was = now;
    };
    document.addEventListener("fullscreenchange", changed);
    return () => document.removeEventListener("fullscreenchange", changed);
  }, [ref]);

  useEffect(() => {
    if (!filled) return undefined;
    filling = ref.current;
    // Listened for on the window, after the document's own listeners, so an Escape that
    // closed a menu inside the desk (and said so) does not also take the desk out of it.
    const escape = (event: KeyboardEvent): void => {
      if (event.key === "Escape" && !event.defaultPrevented) leave();
    };
    window.addEventListener("keydown", escape);
    return () => {
      window.removeEventListener("keydown", escape);
      if (filling === ref.current) filling = null;
    };
  }, [ref, filled, leave]);

  const toggle = useCallback(() => {
    const node = ref.current;
    if (node === null) return;
    if (filled) {
      leave();
      return;
    }
    setFilled(true);
    // Asked for, not waited on: refused or never answered, the element already fills the window.
    if (document.fullscreenEnabled) void node.requestFullscreen().catch(() => undefined);
  }, [ref, filled, leave]);

  return { on: filled, toggle };
}

/**
 * Whether a key pressed at `target` belongs to a control there -- a field being typed into, a
 * menu, a button Space already presses, the editor's own board -- rather than to the page.
 */
function heldByControl(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.isContentEditable ||
    ["INPUT", "TEXTAREA", "SELECT", "BUTTON"].includes(target.tagName) ||
    target.closest("[role=application]") !== null
  );
}

/**
 * The space bar plays and pauses, as it does in every player and notation program.
 *
 * A page can hold more than one desk, and one press must start one of them, so a desk answers
 * only while `mine` -- or, whatever `mine` says, while it is the one on the whole screen,
 * since that is the only desk the reader can see. A key meant for a control is left to it.
 */
export function useSpaceToPlay(
  ref: RefObject<HTMLElement>,
  mine: boolean,
  toggle: () => void,
): void {
  useEffect(() => {
    const pressed = (event: KeyboardEvent): void => {
      if (event.key !== " " || event.repeat || heldByControl(event.target)) return;
      const shown = filling;
      if (shown === null ? !mine : shown !== ref.current) return;
      event.preventDefault();
      toggle();
    };
    window.addEventListener("keydown", pressed);
    return () => window.removeEventListener("keydown", pressed);
  }, [ref, mine, toggle]);
}
